import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Spinner, Alert } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'react-toastify';
import { LocalizedLink } from '../components/LocalizedLink';
import NonIndexableHelmet from '../components/NonIndexableHelmet';
import { getTokenMeta } from '../data/nonIndexableMeta';
import { useLocale } from '../contexts/LocaleContext';
import { localizePath } from '../utils/localePaths';

const VerifyEmailPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const { localePrefix } = useLocale();
  const { t } = useTranslation('auth');
  const [status, setStatus] = useState('verifying');
  const [error, setError] = useState('');

  const dashPath = (role) => localizePath(role === 'admin' ? '/admin' : '/dashboard', localePrefix);

  useEffect(() => {
    const verifyEmail = async () => {
      try {
        const response = await authAPI.verifyEmail(token);

        const authToken = response.data.token;
        const userData = response.data.user;

        login(authToken, userData);
        setStatus('success');
        toast.success(t('verify.toastOk'));

        setTimeout(() => {
          navigate(dashPath(userData.role), { replace: true });
        }, 2000);
      } catch (err) {
        console.error('Error verifying email:', err);
        setStatus('error');
        setError(err.response?.data?.message || t('magicLink.invalidLink'));
      }
    };

    if (token) {
      verifyEmail();
    }
  }, [token, login, navigate, localePrefix]);

  return (
    <div className="auth-page-gradient">
      <NonIndexableHelmet {...getTokenMeta('/verify-email')} />
      <Container>
        <Row className="justify-content-center align-items-center auth-page-shell">
          <Col md={6} lg={5}>
            <Card className="shadow-lg border-0 rounded-lg">
              <Card.Body className="p-5 text-center">
                {status === 'verifying' && (
                  <>
                    <Spinner animation="border" variant="primary" style={{ width: '4rem', height: '4rem' }} />
                    <h3 className="mt-4 fw-bold">{t('verify.verifying')}</h3>
                    <p className="text-muted">{t('verify.wait')}</p>
                  </>
                )}

                {status === 'success' && (
                  <>
                    <div style={{ fontSize: '4rem', color: '#28a745' }}>
                      <i className="bi bi-check-circle-fill" />
                    </div>
                    <h3 className="mt-3 fw-bold text-success">{t('verify.successTitle')}</h3>
                    <Alert variant="success" className="mt-3 text-start">
                      <strong>{t('verify.welcomeTitle')}</strong>
                      <p className="mb-0 mt-2">{t('verify.welcomeBody')}</p>
                    </Alert>
                    <p className="text-muted">{t('verify.redirect')}</p>
                  </>
                )}

                {status === 'error' && (
                  <>
                    <div style={{ fontSize: '4rem', color: '#dc3545' }}>
                      <i className="bi bi-x-circle-fill" />
                    </div>
                    <h3 className="mt-3 fw-bold text-danger">{t('verify.errorTitle')}</h3>
                    <Alert variant="danger" className="mt-3 text-start">
                      {error}
                    </Alert>
                    <p className="text-muted mt-3">{t('verify.errorHint')}</p>
                    <div className="d-grid gap-2 mt-4">
                      <LocalizedLink to="/register/" className="btn btn-primary btn-lg">
                        <i className="bi bi-arrow-left me-2" />
                        {t('verify.retryRegister')}
                      </LocalizedLink>
                      <LocalizedLink to="/login/" className="btn btn-outline-primary btn-lg">
                        {t('verify.hasAccount')}
                      </LocalizedLink>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default VerifyEmailPage;
